import React, { useState } from 'react';
import { aiApi } from '../../api/ai';

/**
 * Panel de chat con analista IA — para profundizar en una pregunta específica
 */
export default function ChatPanel({ contextQuestion, currentAnswer, onUpdateAnswer }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      // Add context about what we're discussing
      const contextMessages = [
        {
          role: 'user',
          content: `Estoy respondiendo a la pregunta: "${contextQuestion}". Mi respuesta actual es: "${currentAnswer || 'aún no he respondido'}". Ayúdame a profundizar.`,
        },
        ...updatedMessages,
      ];

      const response = await aiApi.sendInterviewMessage(contextMessages);
      const assistantMessage = { role: 'assistant', content: response.data.response };
      setMessages([...updatedMessages, assistantMessage]);
    } catch (error) {
      const errorMessage = { role: 'assistant', content: '⚠️ Error al comunicarse con la IA.' };
      setMessages([...updatedMessages, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const applyAsAnswer = (content) => {
    onUpdateAnswer(content);
  };

  return (
    <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
      <div className="p-3 bg-purple-50 border-b border-purple-200">
        <p className="text-sm font-medium text-purple-700">🤖 Chat con Analista IA</p>
        <p className="text-xs text-purple-500 mt-0.5">Pregunta para profundizar en: {contextQuestion}</p>
      </div>

      {/* Messages */}
      <div className="h-64 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <p className="text-sm text-gray-400 text-center mt-8">
            Pregunta algo para obtener más detalle sobre este punto.
          </p>
        )}
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              <p className="whitespace-pre-wrap">{msg.content}</p>
              {msg.role === 'assistant' && (
                <button
                  onClick={() => applyAsAnswer(msg.content)}
                  className="mt-1 text-xs text-purple-600 hover:text-purple-700 underline"
                >
                  Usar como respuesta
                </button>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-3 py-2 bg-gray-100 rounded-lg">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-gray-200 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe tu pregunta..."
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
          disabled={loading}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          Enviar
        </button>
      </div>
    </div>
  );
}
